import { useState, useCallback } from 'react'
import { Divider, SidebarItem } from 'Core/Workbench'

export type SidebarAccessorType = {
    items: (SidebarItem | Divider)[]
    activeItemId?: string
    addItem: (item: SidebarItem | Divider, placement?: 'start' | 'end') => void
    updateItem: (id: string, newItem: SidebarItem | Divider) => void
    changeActiveItemId: (
        setter: ((prev?: string) => string | undefined) | (string | undefined)
    ) => void
    addDivider: () => void
}

export const useSidebar = (): SidebarAccessorType => {
    const [items, setItems] = useState<(SidebarItem | Divider)[]>([])
    const [activeItemId, setActiveItemId] = useState<string>()
    const addItem = useCallback(
        (item: SidebarItem | Divider, placement: 'start' | 'end' = 'end') => {
            setItems(curr => {
                return placement === 'end' ? [...curr, item] : [item, ...curr]
            })
        },
        []
    )

    const updateItem = useCallback((id: string, newItem: SidebarItem | Divider) => {
        setItems(curr =>
            curr.map(item =>
                !(item as SidebarItem).id || (item as SidebarItem).id !== id ? item : newItem
            )
        )
    }, [])

    const changeActiveItemId = useCallback(
        (setter: ((prev?: string) => string | undefined) | (string | undefined)) => {
            setActiveItemId(prev => {
                const item = !setter || typeof setter === 'string' ? setter : setter(prev)
                if (item === undefined) {
                    return item
                }
                /**
                 * Only set the active item if it exists in the items list
                 */
                if (
                    items.some(it => !(it as Divider).isDivider && (it as SidebarItem).id === item)
                ) {
                    return item
                }
                return undefined
            })
        },
        [items]
    )

    const addDivider = useCallback(() => {
        addItem({ isDivider: true })
    }, [addItem])

    return {
        items,
        activeItemId,
        addItem,
        updateItem,
        changeActiveItemId,
        addDivider,
    }
}
